import { CFunction } from "@microchip/scf-interface";

/**
 * @param str [string] the string to search within
 * @param find [string] the string segment to replace
 * @param replace [string] the string that will replace all instances of 'find'
 * @returns the same string with all instances of 'find' replaced by 'replace'
 */
export const replaceAll = (str: string, find: string, replace: string): string => {
    // leveraging JavaScript string arithemtic
    return str.split(find).join(replace);
};

/**
 * @param obj [json object] the object (a.k.a. Record<Key:string, Value:unknown>) to evaluate
 * @returns true if the object is empty; false otherwise
 */
export const isEmptyObject = (obj: Record<string, unknown> | undefined): boolean => {
    if (obj) {
        return Object.keys(obj).length === 0;
    }
    return false;
};

/**
 *
 * @param dirtyPayload
 */
export const cleanFunctionAPI = (
    dirtyPayload: CFunction | "unimplemented" | undefined,
): CFunction | "unimplemented" => {
    if (dirtyPayload !== undefined && typeof dirtyPayload !== "string") {
        return dirtyPayload as CFunction;
    } else {
        return "unimplemented";
    }
};

/**
 * @param functionAPI [any] the api object that needs to be checked for validity
 * @param moduleName [string | undefined] the name of the module taken form the modules processes payload
 * @returns if API is visible then return the name of it, otherwise return "unimplemented" function name
 */
export const parseFunctionAPI = (
    functionAPI: CFunction | "unimplemented",
    moduleName: string | undefined,
): string => {
    if (functionAPI === "unimplemented" || moduleName === undefined) {
        return "unimplemented";
    } else {
        return `${moduleName}_${functionAPI.name}`;
    }
};

/**
 * @param decimal [number] a decimal number (No Floats)
 * @returns a string version of the number in hexidecimal format
 */
export const decimal2Hex = (decimal: number): string => {
    const hex = decimal.toString(16).toUpperCase();

    if (hex.length === 1) {
        return `0x0${hex}`;
    } else {
        return `0x${hex}`;
    }
};

/**
 * @param address [number | undefned] the value in decimal from the UI componenet for the vector address
 * @returns undefined if the value is set to 0 meaning we will not use it and return the no-hex format hex values
 * @description this will produce a range that represents the boot space of flash. Ex: 0-7FF
 */
export const parseResetVector = (address: number | undefined): string | undefined => {
    if (address != undefined && address > 1) {
        // convert JS decimal value to hex using vector helper
        const addressEnd = decimal2Hex(address - 1).replace("0x", "");
        return `0-${addressEnd}`;
    }
    // return undefined to cancel the setting
    return undefined;
};

export const parsePIC16ResetVector = (address: number): string | undefined => {
    let addressBuilder = "";
    for (let i = 0; i < address; i += 0x800) {
        if (i + 0x800 >= address) {
            addressBuilder =
                addressBuilder +
                `${decimal2Hex(i).replace("0x", "")}-${decimal2Hex(address - 1).replace(
                    "0x",
                    "",
                )}`;
        } else {
            addressBuilder =
                addressBuilder +
                `${decimal2Hex(i).replace("0x", "")}-${decimal2Hex(i + 0x800 - 1).replace(
                    "0x",
                    "",
                )},`;
        }
    }

    return addressBuilder;
};

/**
 * @param data [string | json object | undefined] the data returned from the pins data component structure
 * @returns return true if the data is some form of empty.
 */
export const isPinsDataEmpty = (
    data: string | Record<string, unknown> | undefined,
): boolean => {
    if (data === "{}" || data === undefined) {
        return true;
    } else if (isEmptyObject(data as Record<string, unknown>)) {
        return true;
    }
    return false;
};

/**
 * @function hasEEPROM
 * @description This function is checking for a valid eeprom size for the device we support
 *      Note: this could be further improved on if needed for more device support in the future but for now 256 is the minimum size we support
 * @returns boolean
 */
export const hasEEPROM = (eeprom_data: number | undefined): boolean => {
    if (eeprom_data !== undefined && eeprom_data > 0) {
        return true;
    }
    return false;
};
